import { CircularProgress, Tooltip, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import type { Address } from '@wagmi/core';
import BigNumber from 'bignumber.js';
import type { FC, PropsWithChildren } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { waitForTransaction } from 'wagmi/actions';

import { ordersApi } from '../../api';
import { Web3StateContext } from '../../contexts/app-state-context/web3-state/context';
import { useWeb3State } from '../../hooks';
import { useSeaport } from '../../hooks/seaport-hook';
import type { Asset } from '../../lib/graph/MogulMarketplace';
import { Button as WhimsicalButton } from '../core/button';
import styles from './NftOrders.module.css';
import { getOrderRowExpirationByEndTime } from './NftOrders.utils';

interface NftOrdersProps {
  nftContractAddress: string;
  nftId: string;
  walletAddress: string;
  asset?: Asset;
  floorPrice?: string;
  setBestOrderPrice: (bestPrice: string) => void;
}

const NftOrderRowTypography: FC<PropsWithChildren<{}>> = ({ children }) => (
  <Typography
    variant="body2"
    style={{
      fontSize: 16,
      margin: 'auto 0.5rem auto 0',
    }}
  >
    {children}
  </Typography>
);

const getOrderRow: (
  order: any,
  floorPrice?: string,
) => (string | JSX.Element)[] = ({ parameters }, floorPrice) => {
  const offerer = parameters.offerer;
  const addressToShow = `${offerer.slice(0, 6)}...${offerer.slice(-4)}`;

  const amount = new BigNumber(parameters.offer[0].startAmount).div(10 ** 18);

  const floorPercentDifference =
    amount && floorPrice ? amount.toNumber() / Number(floorPrice) - 1 : null;
  const isFloorBelow = (floorPercentDifference ?? 0) < 0;

  return [
    <Tooltip
      title={
        <p style={{ fontSize: 16 }}>
          {new BigNumber(amount).toString(10)} MOGUL
        </p>
      }
      key="amount"
    >
      <p style={{ margin: 'auto 0.5rem auto 0' }}>
        {amount.toFixed(2, 1)}
        {amount.isLessThan('0.01') ? '…' : ''} MOGUL
      </p>
    </Tooltip>,
    parameters.consideration[0].startAmount,
    floorPercentDifference != null
      ? `${(floorPercentDifference * 100 * (isFloorBelow ? -1 : 1)).toFixed(
          0,
        )}% ${isFloorBelow ? 'below' : 'above'}`
      : '—',
    getOrderRowExpirationByEndTime(parameters.endTime),
    addressToShow,
  ];
};

const NftOrderRow = ({
  content,
  order,
  ownedAssets,
  floorPrice,
  reload,
}: {
  content?: string[];
  order?: any;
  ownedAssets?: number;
  floorPrice?: string;
  reload?: () => void;
}) => {
  const [loading, setLoading] = useState<boolean>(false);

  const isEnoughAssets =
    ownedAssets != null &&
    ownedAssets >= Number(order?.parameters.consideration[0].startAmount);

  const { fulfillOrder } = useSeaport();

  const { walletDetails } = useWeb3State();

  const handleFulfill = async () => {
    if (!order || !fulfillOrder) {
      return;
    }

    setLoading(true);

    // if there is an error, we try to fulfill old order w/o seller
    let transactionHash: string | undefined = undefined;
    let errorMessage: string | undefined = undefined;
    try {
      const seaportResult = await fulfillOrder(order);
      transactionHash = seaportResult.hash;
    } catch (err) {
      if (err instanceof Error) {
        errorMessage = err.message;
      }

      console.error('Error while fulfilling order at Seaport', err);
    }

    if (
      errorMessage ===
      'The offerer does not have the amount needed to create or fulfill.'
    ) {
      alert('The offerer does not have the amount needed to fulfill');
      setLoading(false);
      return;
    }

    if (!transactionHash) {
      alert('Error while fulfilling order');
    } else {
      await waitForTransaction({ hash: transactionHash as Address });
    }

    try {
      await ordersApi.fulfillOrder({
        orderId: order.id,
        network: walletDetails.chain.type,
        transactionHash,
      });
    } catch (err) {
      console.error('Error while sending fulfilled order', err);
    }

    setLoading(false);
    reload?.();
  };

  return (
    <div
      className={`${!content && styles.orderRow} ${styles.orderCommonRow} ${
        ownedAssets ? styles.longOrderRow : styles.shortOrderRow
      }`}
    >
      {(content ?? getOrderRow(order, floorPrice))?.map((item, i) =>
        typeof item === 'string' ? (
          <NftOrderRowTypography key={i}>{item}</NftOrderRowTypography>
        ) : (
          item
        ),
      )}
      {order && ownedAssets ? (
        loading ? (
          <div className={'flex justify-end items-center'}>
            <CircularProgress />
          </div>
        ) : (
          <WhimsicalButton
            disabled={!isEnoughAssets}
            onClick={handleFulfill}
            className={styles.acceptButton}
          >
            Accept
          </WhimsicalButton>
        )
      ) : null}
    </div>
  );
};

const NftOrderHeader = ({ ownedAssets }: { ownedAssets?: number }) => (
  <NftOrderRow
    content={['Price', 'Quantity', 'Floor Difference', 'Expiration', 'From']}
    ownedAssets={ownedAssets}
  />
);

export const NftOrders: FC<NftOrdersProps> = ({
  nftContractAddress,
  nftId,
  walletAddress,
  asset,
  floorPrice,
  setBestOrderPrice,
}) => {
  const ownedAssets = useMemo(
    () =>
      asset?.owners?.find(
        ({ account: { address } }) => address === walletAddress,
      )?.quantity ?? 0,
    [asset, walletAddress],
  );

  const ordersPerPage = 5;
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);

  const loadMoreOrders = async (reload: boolean = false) => {
    const res = await ordersApi.listOrders({
      nftContractAddress,
      nftId,
      limit: ordersPerPage,
      offset: reload ? 0 : orders.length,
    });

    setOrders([...(reload ? [] : orders), ...res.data.data]);
    setOrdersCount(res.data.meta.totalCount);
    setBestOrderPrice((res.data.meta as any).bestOffer ?? '0');
  };

  useEffect(() => {
    void loadMoreOrders();
  }, []);

  return (
    <div className={styles.ordersContainer}>
      <Typography
        variant="h1"
        style={{
          fontSize: 32,
          marginBottom: 10,
        }}
      >
        Offers ({ordersCount})
      </Typography>
      <div className={styles.ordersBackground} id="scrollableOrders">
        <NftOrderHeader ownedAssets={ownedAssets} />
        <InfiniteScroll
          dataLength={orders.length}
          hasMore={orders.length < ordersCount}
          next={loadMoreOrders}
          loader={
            <div>
              <Skeleton height={75} />
            </div>
          }
          scrollableTarget="scrollableOrders"
          style={{ overflow: undefined }}
        >
          {orders.map((order, i) => (
            <NftOrderRow
              key={i}
              order={order}
              ownedAssets={ownedAssets}
              floorPrice={floorPrice}
              reload={() => loadMoreOrders(true)}
            />
          ))}
        </InfiniteScroll>
      </div>
    </div>
  );
};
